import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle screen size detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial value
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation variants
  const navbarVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  const logoVariants = {
    normal: { scale: 1 },
    hover: { 
      scale: 1.05, 
      filter: 'drop-shadow(0 0 10px rgba(30, 144, 255, 0.6))',
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  };

  const mobileMenuVariants = {
    closed: { opacity: 0, x: '100%' },
    open: { opacity: 1, x: 0, transition: { duration: 0.3 } }
  };

  const menuItemVariants = {
    closed: { opacity: 0, x: 20 },
    open: i => ({ 
      opacity: 1, 
      x: 0, 
      transition: { delay: i * 0.1, duration: 0.3 } 
    })
  };

  const buttonVariants = {
    closed: { opacity: 0, y: 20 },
    open: { 
      opacity: 1, 
      y: 0, 
      transition: { delay: 0.4, duration: 0.3 } 
    }
  };

  return (
    <motion.header
      className={`fixed w-full -top-6 z-50 transition-all duration-300 ${isMobile ? 'bg-white shadow-sm py-0 h-28' : scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm py-0 h-28' : 'bg-transparent py-0'}`}
      initial="hidden"
      animate="visible"
      variants={navbarVariants}
    >
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        {/* Logo - Always visible on mobile */}
        <Link to="/" className="relative z-10">
          <motion.img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Nexlyx"
            className="h-36 md:h-36 w-auto transition-all duration-300"
            variants={logoVariants}
            initial="normal"
            whileHover="hover"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link to="/" className="nav-link relative font-medium text-dark-gray hover:text-primary transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            Accueil
          </Link>
          <Link to="/services" className="nav-link relative font-medium text-dark-gray hover:text-primary transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            Services
          </Link>
          <Link to="/portfolio" className="nav-link relative font-medium text-dark-gray hover:text-primary transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            Portfolio
          </Link>
          <Link to="/about" className="nav-link relative font-medium text-dark-gray hover:text-primary transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            À propos
          </Link>
          <Link to="/contact" className="nav-link relative font-medium text-dark-gray hover:text-primary transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            Contact
          </Link>
          <Link to="/audit" className="ml-4">
            <motion.button 
              className="bg-gradient-to-r from-primary to-blue-700 text-white px-5 py-2 rounded-full font-medium shadow-md hover:shadow-lg transition-all duration-300"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(30, 144, 255, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              Expertise gratuite
            </motion.button>
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <motion.button
          className="md:hidden text-gray-800 bg-white p-2 rounded-full shadow-md focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </motion.button>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="fixed inset-0 top-20 bg-white shadow-lg z-40 md:hidden flex flex-col pt-6 px-6"
              initial="closed"
              animate="open"
              exit="closed"
              variants={mobileMenuVariants}
            >
              <div className="flex flex-col space-y-4">
                <motion.div custom={0} variants={menuItemVariants}>
                  <Link to="/" className="block py-3 text-lg font-medium text-dark-gray hover:text-primary border-b border-gray-200">
                    Accueil
                  </Link>
                </motion.div>
                <motion.div custom={1} variants={menuItemVariants}>
                  <Link to="/services" className="block py-3 text-lg font-medium text-dark-gray hover:text-primary border-b border-gray-200">
                    Services
                  </Link>
                </motion.div>
                <motion.div custom={2} variants={menuItemVariants}>
                  <Link to="/portfolio" className="block py-3 text-lg font-medium text-dark-gray hover:text-primary border-b border-gray-200">
                    Portfolio
                  </Link>
                </motion.div>
                <motion.div custom={3} variants={menuItemVariants}>
                  <Link to="/about" className="block py-3 text-lg font-medium text-dark-gray hover:text-primary border-b border-gray-200">
                    À propos
                  </Link>
                </motion.div>
                <motion.div custom={4} variants={menuItemVariants}>
                  <Link to="/contact" className="block py-3 text-lg font-medium text-dark-gray hover:text-primary border-b border-gray-200">
                    Contact
                  </Link>
                </motion.div>
                <motion.div className="mt-6" variants={buttonVariants}>
                  <Link to="/audit" className="block">
                    <button className="w-full bg-gradient-to-r from-primary to-blue-700 text-white py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-all duration-300">
                      Expertise gratuite
                    </button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;